import {
  array,
  InferInput,
  InferOutput,
  number,
  object,
  optional,
  pipe,
  safeParse,
  string,
  transform,
  ValiError,
} from 'valibot';
import { AlbumItemSchema, SortSchema } from '../apiSchema.ts';
import { getOffsetNumber } from '../utils.ts';

export const MeLibraryAlbumsRequestArgsSchema = object({
  limit: optional(number()),
  offset: optional(number()),
  sort: optional(SortSchema),
});

export type MeLibraryAlbumsRequestArgs = InferOutput<
  typeof MeLibraryAlbumsRequestArgsSchema
>;

export const MeLibraryAlbumsResponseSchema = pipe(
  object({
    data: array(AlbumItemSchema),
    next: optional(string()),
    meta: object({
      total: number(),
    }),
  }),
  transform(({ data, meta, next }) => {
    return {
      albums: data,
      total: meta.total,
      next: next === undefined ? undefined : getOffsetNumber(next),
    };
  }),
);

export type MeLibraryAlbumsResponse = InferOutput<
  typeof MeLibraryAlbumsResponseSchema
>;

export const MeLibraryAlbumsAPISchema = object({
  data: MeLibraryAlbumsResponseSchema,
});

const initParams = {
  limit: Number(import.meta.env.VITE_APP_MAX_ALBUMS ?? 10),
  offset: 0,
  sort: 'name',
};

export const $getMeLibraryAlbums = async (
  player: MusicKit.MusicKitInstance,
  params: MeLibraryAlbumsRequestArgs,
): Promise<MeLibraryAlbumsResponse> => {
  const p = safeParse(MeLibraryAlbumsRequestArgsSchema, params);
  if (!p.success) {
    throw new ValiError(p.issues);
  }
  const limit = p.output.limit ?? initParams.limit;
  const offset = p.output.offset ?? initParams.offset;

  const res = await player.api.music('/v1/me/library/albums', {
    limit,
    offset,
  }) as unknown as InferInput<typeof MeLibraryAlbumsAPISchema>;

  const parsed = safeParse(MeLibraryAlbumsAPISchema, res);
  if (!parsed.success) {
    throw new ValiError(parsed.issues);
  }
  const data = parsed.output.data;
  return data;
};
